import React from 'react'
import { Alert, Skeleton, Typography } from 'antd'
import { format } from 'date-fns'

import { getDateFnsLocale } from '@vacationtracker/shared/i18n'

import { IUpcomingInvoiceInformation } from '@vacationtracker/shared/types/stripe'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { UpdateSeats } from '../../../../components/update-seats'
import { useCurrency } from '../../../../hooks/use-currency'

const { Paragraph } = Typography

interface INoOfUsersWarrningMessage {
  noOfUsers: number
  totalNumberOfUsersAfterImport: number
  upcomingInvoiceInfo: IUpcomingInvoiceInformation | null
  isLoadingUpcomingInvoiceInfo: boolean
  isAnnualBilling: boolean
  formatMessage: Function
  locale: LocaleEnum
  overSeatsLimit: boolean
  seatsLimit: number | undefined
  isTrial: boolean
  stripeCurrentPeriodEnd: string
  importAllUsers?: boolean
  showAdditionalBillingInfo?: boolean
  submitBilling?: (seats: number) => void
  numberOfActiveUsers?: number
}

export const NoOfUsersWarrningMessage = ({
  noOfUsers,
  formatMessage,
  upcomingInvoiceInfo,
  isLoadingUpcomingInvoiceInfo,
  isAnnualBilling,
  showAdditionalBillingInfo = false,
  locale,
  overSeatsLimit,
  seatsLimit,
  submitBilling,
  importAllUsers,
  numberOfActiveUsers,
  isTrial,
  stripeCurrentPeriodEnd,
}: INoOfUsersWarrningMessage): React.ReactElement => {
  const { formatPrice } = useCurrency()
  const OverSeatsLimit = () => {
    return (
      <Alert
        message={
          <>
            {formatMessage(
              {
                id: importAllUsers ? 'components.importUsersForm.warningOfUsersImportOverSeatsLimitImportAll' : 'components.importUsersForm.warningOfUsersImportOverSeatsLimit',
              },
              { seats: seatsLimit }
            )}
            <UpdateSeats
              numberOfSeats={seatsLimit}
              numberOfActiveUsers={numberOfActiveUsers}
              onSubmit={(seats) => submitBilling && submitBilling(seats)}
              isAnnualBilling={isAnnualBilling}
              locale={locale}
              inputLayout={{
                xl: { span: 6 },
                lg: { span: 10 },
                md: { span: 10 },
                sm: { span: 24 },
                xs: { span: 24 },
              }}
            />
          </>
        }
        type="warning"
      />
    )
  }

  const NoBillingChanges = () => {
    return (
      <Alert
        message={formatMessage(
          { id: 'components.importUsersForm.warningOfUsersImportNoChange' },
          {
            noOfUsers,
          }
        )}
        type="success"
        showIcon
      />
    )
  }

  if (overSeatsLimit) {
    return <OverSeatsLimit />
  }

  if (isLoadingUpcomingInvoiceInfo) {
    return <Skeleton.Button size="large" shape="square" style={{ width: '20vw' }} active />
  }

  if (noOfUsers === 0) {
    return <></>
  }

  if (upcomingInvoiceInfo) {
    if (upcomingInvoiceInfo.isTrial) {
      return (
        <Alert
          message={formatMessage(
            { id: 'components.importUsersForm.warningOfUsersImportTrial' },
            {
              amount: formatPrice(Number(upcomingInvoiceInfo?.nextInvoiceAmount)),
              startDate: format(new Date(upcomingInvoiceInfo?.nextInvoiceDate as string), 'PPP', { locale: getDateFnsLocale(locale) }),
            }
          )}
          type={noOfUsers > 50 ? 'warning' : 'info'}
          showIcon
        />
      )
    } else if (upcomingInvoiceInfo.forPlan === SubscriptionPlanEnum.freemium) {
      return <Alert message={formatMessage({ id: 'components.importUsersForm.warningConfirmImport' }, { noOfUsers })} type="info" showIcon />
    } else if (upcomingInvoiceInfo.isSameInvoiceAmount) {
      return <NoBillingChanges />
    } else if (isAnnualBilling) {
      return (
        <Alert
          message={
            <>
              {showAdditionalBillingInfo && (
                <Paragraph>
                  {formatMessage(
                    { id: 'components.importUsersForm.warningOfUsersImportAnnualProrateAdditionalNewBucket' },
                    {
                      amount: formatPrice(Number(upcomingInvoiceInfo?.nextInvoiceAmount)),
                      noOfUsers,
                      startDate: format(new Date(upcomingInvoiceInfo?.nextInvoiceDate as string), 'PPP', { locale: getDateFnsLocale(locale) }),
                    }
                  )}
                </Paragraph>
              )}
              <div>
                {formatMessage(
                  { id: 'components.importUsersForm.warningOfUsersImportAnnualProrateAdditionalNoOfUsers' },
                  {
                    proratedAmount: formatPrice(Number(upcomingInvoiceInfo?.prorateAmount)),
                  }
                )}
              </div>
            </>
          }
          type="info"
          showIcon
        />
      )
    } else {
      return (
        <Alert
          message={formatMessage(
            { id: 'components.importUsersForm.warningOfUsersImportProrateAdditionalNewBucketMonthly' },
            {
              noOfUsers,
              amount: formatPrice(Number(upcomingInvoiceInfo?.nextInvoiceAmount)),
              startDate: format(new Date(upcomingInvoiceInfo?.nextInvoiceDate as string), 'PPP', { locale: getDateFnsLocale(locale) }),
            }
          )}
          type={noOfUsers > 50 ? 'warning' : 'info'}
          showIcon
        />
      )
    }
  }

  if (isTrial && stripeCurrentPeriodEnd) {
    return (
      <Alert
        message={formatMessage(
          { id: 'components.importUsersForm.warningOfUsersImportTrialWithoutPaymentMethod' },
          { stripeCurrentPeriodEnd: format(new Date(Number(stripeCurrentPeriodEnd) * 1000), 'MMMM do yyyy') }
        )}
        type="info"
        showIcon
        style={{ marginBottom: '20px' }}
      />
    )
  }

  return <></>
}
